'use client';

import { EditableContent } from '../../edit/EditableContent';
import { defaultAllergyFields } from '../../lib/defaultFormFields';
import { parseFormFields } from '../../lib/parseFormFields';
import { pickBlockField } from '../../lib/localized';
import { pickChrome } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';
import { FormFieldControl } from './FormFieldControls';
import { SiteLeadForm } from './SiteLeadForm';

export function AllergyCaptureSecureForm({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const consent = pickBlockField(block.content, 'consent', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const target = isActive ? activeEditTarget : null;
  const operational = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  const fields =
    operational.length > 0
      ? operational.filter((field) => field.name !== 'consent')
      : defaultAllergyFields(locale);
  const consentFromFields = operational.find((field) => field.name === 'consent');
  const formKey = block.dataBinding?.kind === 'form' ? block.dataBinding.formKey : 'allergy';

  return (
    <section
      className={['ws-allergy', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      id="allergies"
    >
      <div className="ws-allergy__inner">
        <EditableContent
          as="h2"
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-allergy__title"
        >
          {heading}
        </EditableContent>
        <EditableContent
          fieldKey="supporting"
          editable={editable}
          activeEditTarget={target}
          className="ws-allergy__subtitle"
        >
          {supporting}
        </EditableContent>
        <SiteLeadForm
          locale={locale}
          formKey={formKey}
          editable={editable}
          className="ws-allergy__form"
        >
          {fields.map((field) => (
            <FormFieldControl
              key={field.name}
              field={field}
              locale={locale}
              fieldClassName="ws-allergy__field"
              editable={editable}
              activeEditTarget={target}
            />
          ))}
          <label className="ws-allergy__consent">
            <input type="checkbox" name="consent" required />
            {consentFromFields ? (
              <span>{consentFromFields.label}</span>
            ) : (
              <EditableContent
                as="span"
                fieldKey="consent"
                editable={editable}
                activeEditTarget={target}
              >
                {consent || pickChrome('consentFallback', locale)}
              </EditableContent>
            )}
          </label>
          <EditableContent
            as="button"
            type="submit"
            fieldKey="ctaLabel"
            editable={editable}
            activeEditTarget={target}
            className="ws-allergy__submit"
          >
            {ctaLabel || pickChrome('submitFallbackAllergy', locale)}
          </EditableContent>
        </SiteLeadForm>
      </div>
    </section>
  );
}
